<script setup lang="ts">
import { postUploadKycDocument, postUploadKycDocumentS3 } from '../../utilities/api'
import { ref } from 'vue'
import { defineEmits } from 'vue'
import BaseInputFilePond from './BaseInputFilePond.vue'
import { defineProps, reactive, RenderFunction, watch } from 'vue'
import { useField } from 'vee-validate'

const props = withDefaults(
    defineProps<{
        modelValue: string
        label: string
        name: string
        icon?: RenderFunction
        required?: boolean
        required2?: boolean
        camera?: boolean
        rules?: any
        acceptedFileTypes?: string[]
        process: (
            fieldName,
            file,
            metadata,
            load,
            error,
            progress,
            abort,
            transfer,
            options
        ) => Promise<{ abort: () => void }>
    }>(),
    {
        acceptedFileTypes: () => ['image/jpg', 'image/jpeg', 'image/png'],
    }
)

const uploading = ref(false)
watch(uploading, () => emit('update:uploading', uploading.value))

const uploader = ref<InstanceType<typeof BaseInputFilePond> | null>(null)

const myFiles = reactive([])

function flush() {
    myFiles.splice(0)
}

watch(myFiles, () => updateValue(myFiles))
const { errorMessage, handleChange } = useField(props.name, props.rules, { initialValue: props.modelValue })

function updateValue(value) {
    handleChange(value)
    emit('update:modelValue', value)
}

/**
 *
 * @param fieldName
 * @param file
 * @param metadata
 * @param load
 * @param error
 * @param progress
 * @param abort
 * @param transfer
 * @param options
 *
 * docs: https://pqina.nl/filepond/docs/api/server/#process-1
 */
async function process(fieldName, file, metadata, load, error, progress, abort, transfer, options) {
    const controller = new AbortController()
    uploading.value = true
    await postUploadKycDocumentS3(file, {
        // Should call the progress method to update the progress to 100% before calling load
        // Setting computable to false switches the loading indicator to infinite mode
        onUploadProgress: (e) => progress(e.lengthComputable, e.loaded, e.total),
        signal: controller.signal,
    })
        .then(({ data }) => {
            // Should call the load method when done and pass the returned server file id
            // this server file id is then used later on when reverting or restoring a file
            // so your server knows which file to return without exposing that info to the client

            myFiles.push(data)
            emit('uploaded', data)
            uploading.value = false
            load(data)
        })
        .catch(() => {
            error('oh no')
            uploading.value = false
        })

    return {
        abort: () => {
            // This function is entered if the user has tapped the cancel button
            controller.abort()
            // Let FilePond know the request has been cancelled
            abort()
        },
    }
}

const emit = defineEmits(['update:modelValue', 'uploaded', 'update:uploading'])
</script>

<template>
    <BaseInputFilePond
        ref="uploader"
        :process="process"
        :icon="props.icon"
        :name="props.name"
        :label="props.label"
        :required="props.required"
        :required2="props.required2"
        :error-message="errorMessage"
        :accepted-file-types="acceptedFileTypes"
        :camera="camera"
        @flush="flush"
        @update:modelValue="updateValue"
        @update:uploading="emit"></BaseInputFilePond>
</template>
